/**
 * THE Declared Input emptiness verdict (TV1-2453 S0, ADR-201).
 *
 * An empty Declared Input does NOT fail at dispatch: the Rules engine marks the consuming node
 * `inactive` (`teselly-rules-engine/src/rule.ts`, "ABSENCE IS DEACTIVATION"), so a promoted AI
 * rule with an empty input is live and silently never acts. Promotion is therefore refused while
 * this returns anything. The server (`RecordSubGoalPromoted`) and the web's promote button must
 * agree on what "empty" means, so they share this one function instead of two copies.
 *
 * `graph` is the opaque `CandidateGraphPayloadSchema` (the compiler emits
 * `declaredInputs: [{ nodeId, inputName }]` beside `nodes`), so anything that is not an array
 * of declarations with a string `inputName` is tolerated as "declares nothing".
 *
 * A value is EMPTY when it is absent, `undefined`, `null`, a string that is blank after trimming,
 * `[]` or `{}`. The check is SHALLOW: `0`, `false` and `{ a: '' }` are values.
 *
 * @returns the unfilled input names, in declaration order, each named once.
 */
export const unfilledDeclaredInputs = ( graph: unknown, values: unknown ): string[] => {
  const declarations = ( graph as { declaredInputs?: unknown } | null | undefined )?.declaredInputs;
  if ( !Array.isArray( declarations )) return [];
  const valuesByName = ( values && typeof values === 'object' ? values : {}) as Record<string, unknown>;

  const unfilled: string[] = [];
  for ( const declaration of declarations ) {
    const inputName = ( declaration as { inputName?: unknown } | null )?.inputName;
    if ( typeof inputName !== 'string' || unfilled.includes( inputName )) continue;
    if ( isEmptyValue( valuesByName[inputName])) unfilled.push( inputName );
  }
  return unfilled;
};

const isEmptyValue = ( value: unknown ): boolean => {
  if ( value === undefined || value === null ) return true;
  if ( typeof value === 'string' ) return value.trim() === '';
  if ( Array.isArray( value )) return value.length === 0;
  if ( typeof value === 'object' ) return Object.keys( value ).length === 0;
  return false;
};
